import React from "react";
import { resolveKeysToVarNames } from "./VariablePicker";

function isVariableParam(p) {
  return p.type !== "select" && p.type !== "number" && p.type !== "checklist";
}

/** The Configuration canvas: click a role-zone to make it the active target for
 * the Dataset Variables panel, remove assigned chips, tune method/confidence/
 * statistics-and-plots settings, then Reset / Paste Syntax / Run. */
function AnalysisParamsForm({ analysisType, dataGroup, values, onChange, activeZoneName, onSetActiveZone, onReset, onPasteSyntax }) {
  if (!analysisType) return null;
  const variableParams = analysisType.params.filter(isVariableParam);
  const settingParams = analysisType.params.filter((p) => p.type === "select" || p.type === "number");
  const checklistParams = analysisType.params.filter((p) => p.type === "checklist");

  const chipLabel = (key) => (dataGroup[key]?.Qtitle ? `${key} — ${dataGroup[key].Qtitle}` : key);

  const removeChip = (p, key) => {
    if (p.multiple) {
      onChange(p.name, (values[p.name] || []).filter((k) => k !== key));
    } else {
      onChange(p.name, "");
    }
  };

  return (
    <div className="config-canvas">
      <div className="role-zones">
        {variableParams.map((p) => {
          const zoneLabel = p.zone || p.label;
          const assigned = p.multiple ? (values[p.name] || []) : (values[p.name] ? [values[p.name]] : []);
          const isActive = activeZoneName === p.name;
          return (
            <div className={`role-zone ${isActive ? "active" : ""}`} key={p.name}>
              <button className="role-zone-header" onClick={() => onSetActiveZone(p.name)}>
                <span>{zoneLabel.toUpperCase()}{p.required && <span className="analysis-required">*</span>}</span>
                {isActive && <span className="role-zone-active-badge">Click variables to assign</span>}
              </button>
              <div className="role-zone-body">
                {assigned.length === 0 && <span className="role-zone-empty">Empty — select from Dataset Variables</span>}
                {assigned.map((key) => (
                  <span className="role-chip" key={key}>
                    {chipLabel(key)}
                    <button onClick={() => removeChip(p, key)} aria-label="Remove">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {settingParams.length > 0 && (
        <div className="config-settings-row">
          {settingParams.map((p) => (
            <div className="analysis-form-field" key={p.name}>
              <label>{p.label}</label>
              {p.type === "select" ? (
                <select
                  className="analysis-select"
                  value={values[p.name] ?? p.default}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const numeric = p.options.every((o) => typeof o.value === "number");
                    onChange(p.name, numeric ? Number(raw) : raw);
                  }}
                >
                  {p.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input
                  type="number"
                  className="analysis-number-input"
                  value={values[p.name] ?? p.default ?? ""}
                  placeholder={p.optional ? "auto" : undefined}
                  min={1}
                  onChange={(e) => onChange(p.name, e.target.value === "" ? "" : Number(e.target.value))}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {checklistParams.map((p) => (
        <div className="stats-plots-box" key={p.name}>
          <h5>{p.label}</h5>
          {p.items.map((item) => {
            const checked = (values[p.name] || {})[item.name] ?? item.default;
            return (
              <label className="stats-plots-item" key={item.name}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onChange(p.name, { ...(values[p.name] || {}), [item.name]: e.target.checked })}
                />
                {item.label}
              </label>
            );
          })}
        </div>
      ))}

      <div className="config-actions">
        <button className="analysis-reset-btn" onClick={onReset}>Reset</button>
        <button className="analysis-syntax-btn" onClick={onPasteSyntax}>Paste Syntax</button>
      </div>
    </div>
  );
}

export function validateParams(analysisType, values, dataGroup) {
  for (const p of analysisType.params) {
    if (!isVariableParam(p)) continue;
    const raw = values[p.name];
    const resolved = p.multiple
      ? resolveKeysToVarNames(raw || [], dataGroup)
      : resolveKeysToVarNames(raw ? [raw] : [], dataGroup);
    if (p.required && resolved.length === 0) return `"${p.zone || p.label}" is required.`;
    if (p.min && resolved.length < p.min) return `"${p.zone || p.label}" needs at least ${p.min} variable(s).`;
  }
  return null;
}

export function buildParamsPayload(analysisType, values, dataGroup) {
  const out = {};
  for (const p of analysisType.params) {
    if (p.type === "checklist") continue; // display-only toggle, not sent to the backend
    if (p.type === "select") {
      out[p.name] = values[p.name] ?? p.default;
    } else if (p.type === "number") {
      const v = values[p.name];
      out[p.name] = v === "" || v === undefined || v === null ? (p.default ?? null) : v;
    } else if (p.multiple) {
      out[p.name] = resolveKeysToVarNames(values[p.name] || [], dataGroup);
    } else {
      const names = resolveKeysToVarNames(values[p.name] ? [values[p.name]] : [], dataGroup);
      out[p.name] = names[0] || null;
    }
  }
  return out;
}

export function firstZoneName(analysisType) {
  const p = analysisType.params.find(isVariableParam);
  return p ? p.name : null;
}

export function checklistDefaults(analysisType) {
  const out = {};
  analysisType.params.filter((p) => p.type === "checklist").forEach((p) => {
    out[p.name] = {};
    p.items.forEach((item) => { out[p.name][item.name] = item.default; });
  });
  return out;
}

export default AnalysisParamsForm;
